import { createContext, useEffect, useMemo, useState } from "react";
import { captureError } from "src/monitoring/sentry";
import type { PropsWithChildren, ReactNode } from "react";
import type { Configuration } from "../../business/configuration/configuration";
import { API_URL } from "../API";

interface ConfigurationContextType {
  configuration: Configuration;
  loading: boolean;
  /**
   * Re-fetches `/web/configuration`. Callers that mutate configuration-backed
   * data (e.g. the admin feature switch panel) use this so the app-wide
   * configuration doesn't go stale behind them.
   */
  refreshConfiguration: () => void;
}

const defaultConfiguration: Configuration = {
  tags: [],
  featureSwitches: [],
};

export const ConfigurationContext = createContext<ConfigurationContextType>({
  configuration: defaultConfiguration,
  loading: true,
  refreshConfiguration: () => {},
});

export const ConfigurationProvider = ({
  children,
}: PropsWithChildren<ReactNode>) => {
  const [configuration, setConfiguration] =
    useState<Configuration>(defaultConfiguration);
  const [loading, setLoading] = useState(true);

  const fetchConfiguration = () => {
    fetch(`${API_URL}/configuration`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data: Configuration) => {
        setConfiguration(data);
        setLoading(false);
      })
      .catch((e) => {
        captureError(e, "warning");
      });
  };

  useEffect(() => {
    if (!loading || configuration.tags.length > 0) return;

    fetchConfiguration();
  }, []);

  const value = useMemo(
    () => ({ configuration, loading, refreshConfiguration: fetchConfiguration }),
    [configuration, loading],
  );

  return (
    <ConfigurationContext.Provider value={value}>
      {children}
    </ConfigurationContext.Provider>
  );
};
